import type { sfCollection, sfProductByHandle } from "@unlikelystudio/commerce-connector"

import { COLLECTION_METAFIELDS, PRODUCT_METAFIELDS, VARIANT_METAFIELDS } from "~/lib/shopify/constants"
import { VARIANT_COMPONENTS_FIRST, VARIANT_FIRST } from "~/lib/shopify/constants/variant"
import { objectEntries } from "~/utils/object-entries"

export const MEDIA_FIRST = 50

/**
 * Card product metafields keys
 */
export const CARD_PRODUCT_METAFIELDS = [
  PRODUCT_METAFIELDS.TAG,
  PRODUCT_METAFIELDS.SUBTITLE,
  PRODUCT_METAFIELDS.CUSTOM_DEFAULT_IMG,
  PRODUCT_METAFIELDS.CUSTOM_DEFAULT_IMG_HOVER,
  PRODUCT_METAFIELDS.BUNDLE_HIDE_FROM_PRICE_LABEL,
  PRODUCT_METAFIELDS.ASSOCIATED_PRODUCTS,
  PRODUCT_METAFIELDS.DISPLAY_NAME,
  PRODUCT_METAFIELDS.PRODUCT_COLOR,
  PRODUCT_METAFIELDS.PRODUCT_CURTAIN_HANGS,
]

export const PRODUCT_ROOT_FIELDS = {
  includePrices: true,
  includeSelectedOptions: true,
} as const

/**
 * Variant metafields keys
 */
export const VARIANT_ROOT_METAFIELDS = [VARIANT_METAFIELDS.HIDDEN_ON_MARKET, VARIANT_METAFIELDS.COLOR_DOT_IMAGE]

export const VARIANT_COMPONENT_PRODUCT_METAFIELD_KEYS = [
  PRODUCT_METAFIELDS.PRODUCT_NAME_IN_BUNDLE,
  PRODUCT_METAFIELDS.BUNDLE_HIDE_FROM_PRICE_LABEL,
  PRODUCT_METAFIELDS.PRODUCT_NAME_IN_BUNDLE_FOR_DUPLICATE,
  PRODUCT_METAFIELDS.PRODUCT_COLOR,
  PRODUCT_METAFIELDS.EMBROIDERY,
]

export const COLLECTION_METAFIELDS_KEYS = [
  COLLECTION_METAFIELDS.REMOVE_FROM_BREADCRUMB,
  COLLECTION_METAFIELDS.MATERIALS_PANEL,
  COLLECTION_METAFIELDS.EXCLUDED_FILTERS_BY_MARKETS,
]

export const CARD_PRODUCT_GETTER_PARAMS = {
  collectionsFirst: 3,
  imagesFirst: MEDIA_FIRST,
  ...PRODUCT_ROOT_FIELDS,
  metafieldKeys: CARD_PRODUCT_METAFIELDS,
  metafieldVariantsKeys: VARIANT_ROOT_METAFIELDS,
  // WARNING :
  // The less variants we need, the better.
  // It was set previously to 250, but it was causing a lot of performance issues.
  // We should try to keep this number as low as possible.
  // Info : We set it to 1 here just because only one variant is needed in the serializeViewItemPayload function
  variantsFirst: 1,
  // We just need only 1 component to know if the product is a bundle.
  variantComponentsFirst: 1,
  variantComponentProductMetafieldKeys: VARIANT_COMPONENT_PRODUCT_METAFIELD_KEYS,
  metafieldCollectionsKeys: COLLECTION_METAFIELDS_KEYS,
} satisfies Omit<Parameters<typeof sfProductByHandle>[1], "handle">

export const CARD_PRODUCT_COLLECTION_GETTER_PARAMS = {
  // So we do not override the metafieldKeys of collection queries
  ...(Object.fromEntries(objectEntries(CARD_PRODUCT_GETTER_PARAMS).filter(([key]) => key !== "metafieldKeys")) as Omit<
    Parameters<typeof sfProductByHandle>[1],
    "handle" | "metafieldKeys"
  >),
  metafieldProductsKeys: CARD_PRODUCT_GETTER_PARAMS.metafieldKeys,
} satisfies Omit<Parameters<typeof sfCollection>[1], "handle">

export const PRODUCT_PAGE_GETTER_PARAMS = {
  collectionsFirst: 10,
  mediaFirst: MEDIA_FIRST,
  includeDescriptionHtml: true,
  includeTags: true,
  includeQuantityAvailable: true,
  variantsFirst: VARIANT_FIRST,
  variantComponentsFirst: VARIANT_COMPONENTS_FIRST,
  ...PRODUCT_ROOT_FIELDS,
  metafieldKeys: [
    PRODUCT_METAFIELDS.COMPOSITION_ENTRETIEN,
    PRODUCT_METAFIELDS.CONFECTION_SAVOIR_FAIRE,
    PRODUCT_METAFIELDS.LIVRAISON_RETOURS,
    PRODUCT_METAFIELDS.SIZE_CHART,
    PRODUCT_METAFIELDS.EMBROIDERY,
    PRODUCT_METAFIELDS.EMBROIDERY_TITLE,
    PRODUCT_METAFIELDS.EMBROIDERY_DESCRIPTION,
    PRODUCT_METAFIELDS.EMBROIDERY_IMAGES,
    PRODUCT_METAFIELDS.EMBROIDERY_EMBROIDERY_TITLE,
    PRODUCT_METAFIELDS.EMBROIDERY_EMPLACEMENT_TYPE,
    PRODUCT_METAFIELDS.CROSS_SELL_PRODUCTS,
    PRODUCT_METAFIELDS.CROSS_SELL_TITLE,
    PRODUCT_METAFIELDS.PRODUCT_REASSURANCE,
    PRODUCT_METAFIELDS.EDITO_COLLECTION_SLICE,
    PRODUCT_METAFIELDS.BREADCRUMB_COLLECTIONS_ORDER,
    PRODUCT_METAFIELDS.PRODUCT_COLOR_TAG,
    PRODUCT_METAFIELDS.HIDE_VARIANTS_IN_SIMPLE_PRODUCTS,
    PRODUCT_METAFIELDS.HAS_JOIN_STORIES,
    ...CARD_PRODUCT_METAFIELDS,
  ],
  metafieldCollectionsKeys: COLLECTION_METAFIELDS_KEYS,
  metafieldVariantsKeys: VARIANT_ROOT_METAFIELDS,
  variantComponentProductMetafieldKeys: VARIANT_COMPONENT_PRODUCT_METAFIELD_KEYS,
} satisfies Omit<Parameters<typeof sfProductByHandle>[1], "handle">
